import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, FileSignature, Loader2, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { idr } from "../../lib/format";
import { cn } from "../../lib/utils";

const uid = () => Math.random().toString(36).slice(2, 10);
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const newRab = () => ({ id: uid(), section: "", name: "", spec: "", unit: "", volume: "", unit_price: "" });
const newTop = () => ({ id: uid(), kind: "termin", label: "", percent: "", due_days: 14, progress_req: 0 });
const newClause = () => ({ id: uid(), title: "", body: "", enabled: true });
const EMPTY_PARTY = { name: "", company: "", position: "", address: "" };

const DEFAULT_TOP = () => [
  { id: uid(), kind: "dp", label: "Down Payment (Uang Muka)", percent: 45, due_days: 7, progress_req: 0 },
  { id: uid(), kind: "termin", label: "Termin ke-1", percent: 50, due_days: 14, progress_req: 95 },
  { id: uid(), kind: "retention", label: "Masa Pemeliharaan / Retensi", percent: 5, due_days: 14, progress_req: 100 },
];

const NEW_FORM = () => ({
  spk_number: "", title: "", work_name: "", doc_date: "", location: "", project_location: "",
  start_date: "", end_date: "", duration_days: 30, maintenance_days: 30,
  contract_type: "FIXED UNIT PRICE", tax_percent: 11, tax_included: true, contract_value: 0,
  commitment_fee_mode: "none", commitment_fee_value: 0,
  scope: "", terms: "", party1: { ...EMPTY_PARTY }, party2: { ...EMPTY_PARTY },
  rab_items: [newRab()], top_terms: DEFAULT_TOP(), clauses: [], required_signers: [],
});

function docToForm(doc) {
  return {
    ...NEW_FORM(),
    ...Object.fromEntries(["spk_number", "title", "work_name", "location", "project_location", "contract_type", "scope", "terms"]
      .map((k) => [k, doc[k] || ""])),
    doc_date: doc.doc_date || "", start_date: doc.start_date || "", end_date: doc.end_date || "",
    duration_days: doc.duration_days ?? 30, maintenance_days: doc.maintenance_days ?? 30,
    tax_percent: doc.tax_percent ?? 11, tax_included: doc.tax_included ?? true, contract_value: doc.contract_value ?? 0,
    commitment_fee_mode: doc.commitment_fee_mode || "none", commitment_fee_value: doc.commitment_fee_value ?? 0,
    party1: { ...EMPTY_PARTY, ...(doc.party1 || {}) },
    party2: { ...EMPTY_PARTY, ...(doc.party2 || {}) },
    rab_items: (doc.rab_items || []).map((r) => ({ ...newRab(), ...r })),
    top_terms: (doc.top_terms || []).map((t) => ({ ...newTop(), ...t })),
    clauses: (doc.clauses || []).map((c) => ({ ...newClause(), ...c })),
    required_signers: doc.required_signers || [],
  };
}

const INPUT_CLS = "w-full h-8 px-2.5 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";

function Field({ label, testid, className, ...props }) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-[11px] font-medium text-muted-foreground">{label}</label>
      <input data-testid={testid} className={INPUT_CLS} {...props} />
    </div>
  );
}

function Section({ title, hint, open, onToggle, testid, children }) {
  return (
    <section className="rounded-lg border border-border bg-card" data-testid={testid}>
      <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left hover:bg-muted/40 rounded-t-lg transition-colors">
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold">{title}</span>
          {hint && <span className="block text-[11px] text-muted-foreground truncate">{hint}</span>}
        </span>
        {open ? <ChevronUp size={15} className="text-muted-foreground shrink-0" /> : <ChevronDown size={15} className="text-muted-foreground shrink-0" />}
      </button>
      {open && <div className="px-3.5 pb-3.5 pt-2.5 space-y-3 border-t border-border">{children}</div>}
    </section>
  );
}

function PartyCard({ title, subtitle, party, onChange, testid }) {
  return (
    <div className="rounded-md border border-border p-2.5 space-y-2" data-testid={testid}>
      <p className="text-xs font-semibold">{title} <span className="font-normal text-muted-foreground">({subtitle})</span></p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Field label="Nama" testid={`${testid}-name`} value={party.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Nama penandatangan" />
        <Field label="Perusahaan" testid={`${testid}-company`} value={party.company} onChange={(e) => onChange("company", e.target.value)} placeholder="PT ..." />
        <Field label="Jabatan" testid={`${testid}-position`} value={party.position} onChange={(e) => onChange("position", e.target.value)} placeholder="Direktur / Project Manager" />
        <Field label="Alamat" testid={`${testid}-address`} value={party.address} onChange={(e) => onChange("address", e.target.value)} placeholder="Alamat perusahaan" />
      </div>
    </div>
  );
}

export default function SpkBuilder({ project, initial, users, onClose, onSaved }) {
  const [docId, setDocId] = useState(initial?.id || null);
  const [form, setForm] = useState(() => (initial ? docToForm(initial) : NEW_FORM()));
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [clausesLoading, setClausesLoading] = useState(!initial);
  const [open, setOpen] = useState({ info: true, parties: false, rab: true, top: false, clauses: false, signers: false, preview: false });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  useEffect(() => {
    if (!initial) return;
    const t = setTimeout(() => setPreviewKey((k) => k + 1), 400);
    return () => clearTimeout(t);
  }, [initial]);

  useEffect(() => {
    if (initial) return;
    api.get("/spk/default-clauses")
      .then(({ data }) => setForm((f) => (f.clauses.length ? f : { ...f, clauses: data.map((c) => ({ ...newClause(), ...c })) })))
      .catch(() => {})
      .finally(() => setClausesLoading(false));
  }, [initial]);

  const totals = useMemo(() => {
    const rows = form.rab_items.filter((r) => r.name.trim());
    const subtotal = rows.reduce((s, r) => s + Math.round(num(r.volume) * num(r.unit_price)), 0);
    const pct = num(form.tax_percent);
    const tax = form.tax_included ? (pct ? Math.round((subtotal * pct) / (100 + pct)) : 0) : Math.round((subtotal * pct) / 100);
    const grand = form.tax_included ? subtotal : subtotal + tax;
    const hasRab = rows.length > 0;
    const contract = hasRab ? grand : num(form.contract_value);
    const feeRaw = form.commitment_fee_mode === "percent"
      ? Math.round((contract * Math.min(Math.max(num(form.commitment_fee_value), 0), 100)) / 100)
      : form.commitment_fee_mode === "fixed" ? Math.max(0, Math.round(num(form.commitment_fee_value))) : 0;
    const fee = Math.min(feeRaw, contract);
    return { subtotal, tax, grand, hasRab, contract, fee, remaining: Math.max(0, contract - fee) };
  }, [form.rab_items, form.tax_percent, form.tax_included, form.contract_value, form.commitment_fee_mode, form.commitment_fee_value]);

  const pctSum = form.top_terms.reduce((s, t) => s + num(t.percent), 0);
  const topAlloc = totals.fee + form.top_terms.reduce((s, t) => s + Math.round((totals.contract * num(t.percent)) / 100), 0);
  const allocDiff = totals.contract - topAlloc;
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    if (!docId) return;
    api.get(`/projects/${project.id}/terms`).then(({ data }) => setPayment({ paid: data.paid || 0 })).catch(() => {});
  }, [docId, project.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setParty = (p) => (k, v) => setForm((f) => ({ ...f, [p]: { ...f[p], [k]: v } }));
  const updRow = (key, id, patch) => setForm((f) => ({ ...f, [key]: f[key].map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const delRow = (key, id) => setForm((f) => ({ ...f, [key]: f[key].filter((r) => r.id !== id) }));
  const addRow = (key, factory) => setForm((f) => ({ ...f, [key]: [...f[key], factory()] }));
  const moveRow = (key, id, dir) => setForm((f) => {
    const arr = [...f[key]];
    const i = arr.findIndex((r) => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, [key]: arr };
  });
  const toggleSigner = (id) =>
    setForm((f) => ({ ...f, required_signers: f.required_signers.includes(id) ? f.required_signers.filter((x) => x !== id) : [...f.required_signers, id] }));

  const loadDefaultClauses = async () => {
    try {
      const { data } = await api.get("/spk/default-clauses");
      set("clauses", data.map((c) => ({ ...newClause(), ...c })));
      toast.success("17 Pasal standar dimuat");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const payload = () => ({
    spk_number: form.spk_number, title: form.title, work_name: form.work_name,
    doc_date: form.doc_date || null, location: form.location, project_location: form.project_location,
    start_date: form.start_date || null, end_date: form.end_date || null,
    duration_days: Math.round(num(form.duration_days)), maintenance_days: Math.round(num(form.maintenance_days)),
    contract_type: form.contract_type, tax_percent: num(form.tax_percent), tax_included: !!form.tax_included,
    commitment_fee_mode: form.commitment_fee_mode, commitment_fee_value: num(form.commitment_fee_value),
    contract_value: totals.contract, scope: form.scope, terms: form.terms,
    client_name: form.party1.company, vendor_name: form.party2.company,
    party1: form.party1, party2: form.party2,
    rab_items: form.rab_items.filter((r) => r.name.trim()).map((r) => ({ ...r, volume: num(r.volume), unit_price: Math.round(num(r.unit_price)) })),
    top_terms: form.top_terms.filter((t) => t.label.trim()).map((t) => ({ ...t, percent: num(t.percent), due_days: Math.round(num(t.due_days)), progress_req: Math.round(num(t.progress_req)) })),
    top_config: { require_invoice: true, require_kuitansi: true },
    clauses: form.clauses.map((c) => ({ id: c.id, title: c.title, body: c.body, enabled: !!c.enabled })),
    required_signers: form.required_signers,
  });

  const save = async (closeAfter) => {
    if (!form.title.trim()) { toast.error("Judul SPK wajib diisi"); return; }
    setSaving(true);
    try {
      const { data } = docId
        ? await api.patch(`/spk/${docId}`, payload())
        : await api.post(`/projects/${project.id}/spk`, payload());
      setDocId(data.id);
      setPreviewKey((k) => k + 1);
      toast.success("SPK tersimpan — preview diperbarui");
      onSaved();
      if (closeAfter) onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const previewFrame = (cls) => (
    docId ? (
      <iframe key={previewKey} src={`${API_BASE}/spk/${docId}/pdf?cb=${previewKey}`} title="Preview SPK"
        className={cn("w-full rounded-md border border-border bg-white", cls)} data-testid="spk-builder-preview-frame" />
    ) : (
      <div className={cn("w-full rounded-md border border-dashed border-border flex flex-col items-center justify-center text-muted-foreground gap-2", cls)}
        data-testid="spk-builder-preview-empty">
        <FileSignature size={26} className="opacity-40" />
        <p className="text-xs text-center px-6">Simpan SPK terlebih dahulu untuk melihat preview PDF di sini.</p>
      </div>
    )
  );

  const rabAmount = (r) => Math.round(num(r.volume) * num(r.unit_price));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] lg:max-w-7xl h-[92vh] bg-background flex flex-col p-0 gap-0 overflow-hidden" data-testid="spk-builder-dialog">
        <DialogHeader className="px-4 sm:px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <DialogTitle className="font-heading flex items-center gap-2 text-base">
              <FileSignature size={16} className="text-[#1B2A6B] dark:text-blue-300" />
              {docId ? `Edit SPK${initial?.version ? ` (v${initial.version})` : ""}` : "SPK Builder — Dokumen Baru"}
            </DialogTitle>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" data-testid="spk-builder-cancel" onClick={onClose}
                className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Tutup</button>
              <button type="button" data-testid="spk-builder-save" onClick={() => save(false)} disabled={saving}
                className="h-8 px-3 text-sm rounded-md bg-[#1B2A6B] text-white hover:bg-[#16224f] transition-colors disabled:opacity-60 flex items-center gap-1.5">
                {saving && <Loader2 size={13} className="animate-spin" />}Simpan
              </button>
              <button type="button" data-testid="spk-builder-save-close" onClick={() => save(true)} disabled={saving}
                className="h-8 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
                {saving && <Loader2 size={13} className="animate-spin" />}Simpan & Tutup
              </button>
            </div>
          </div>
          {initial && initial.status !== "draft" && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="spk-builder-reset-note">
              Menyimpan perubahan akan mengembalikan status ke Draft dan menghapus tanda tangan yang ada (versi naik).
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 grid lg:grid-cols-2">
          <div className="overflow-y-auto p-4 space-y-3 lg:border-r border-border" data-testid="spk-builder-form">
            <Section title="Informasi Umum" hint="Nomor, judul pekerjaan, tanggal & lokasi" open={open.info} onToggle={() => toggle("info")} testid="spk-builder-sec-info">
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="Nomor SPK" testid="spk-builder-spk-number" value={form.spk_number} onChange={(e) => set("spk_number", e.target.value)} placeholder="05/NAC-SPK/V/2026" />
                <Field label="Judul Dokumen *" testid="spk-builder-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Surat Perjanjian Kontrak Kerja ..." />
                <Field label="Nama Pekerjaan" className="sm:col-span-2" testid="spk-builder-work-name" value={form.work_name} onChange={(e) => set("work_name", e.target.value)} placeholder="Pekerjaan Perkuatan Baja Gedung ..." />
                <Field label="Tanggal Dokumen" testid="spk-builder-doc-date" type="date" value={form.doc_date} onChange={(e) => set("doc_date", e.target.value)} />
                <Field label="Dibuat di (Kota)" testid="spk-builder-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Bekasi Timur" />
                <Field label="Lokasi Proyek" className="sm:col-span-2" testid="spk-builder-project-location" value={form.project_location} onChange={(e) => set("project_location", e.target.value)} placeholder="Alamat lokasi proyek" />
                <Field label="Waktu Pelaksanaan (hari kalender)" testid="spk-builder-duration" type="number" min="0" value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)} />
                <Field label="Masa Pemeliharaan (hari kalender)" testid="spk-builder-maintenance" type="number" min="0" value={form.maintenance_days} onChange={(e) => set("maintenance_days", e.target.value)} />
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground">Sifat Kontrak</label>
                  <select data-testid="spk-builder-contract-type" value={form.contract_type} onChange={(e) => set("contract_type", e.target.value)} className={INPUT_CLS}>
                    <option value="FIXED UNIT PRICE">FIXED UNIT PRICE</option>
                    <option value="LUMP SUM">LUMP SUM</option>
                    <option value="COST PLUS FEE">COST PLUS FEE</option>
                  </select>
                </div>
                <Field label="PPN (%)" testid="spk-builder-tax-percent" type="number" min="0" step="0.1" value={form.tax_percent} onChange={(e) => set("tax_percent", e.target.value)} />
                <label className="sm:col-span-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="spk-builder-tax-included-label">
                  <input type="checkbox" data-testid="spk-builder-tax-included" checked={!!form.tax_included} onChange={(e) => set("tax_included", e.target.checked)} className="accent-[#1B2A6B]" />
                  PPN sudah termasuk dalam nilai kontrak (tax inclusive)
                </label>
                {totals.hasRab ? (
                  <div className="sm:col-span-2 space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">Nilai Kontrak (IDR)</label>
                    <div className="h-8 px-2.5 flex items-center justify-between gap-2 text-sm rounded-md border border-dashed border-[#1B2A6B]/40 bg-[#1B2A6B]/5 dark:bg-blue-300/5" data-testid="spk-builder-contract-value-auto">
                      <span className="font-semibold text-[#1B2A6B] dark:text-blue-300">{idr(totals.grand)}</span>
                      <span className="text-[10px] text-muted-foreground">Otomatis dari Grand Total RAB</span>
                    </div>
                  </div>
                ) : (
                  <Field label="Nilai Kontrak (IDR)" className="sm:col-span-2" testid="spk-builder-contract-value" type="number" min="0" value={form.contract_value} onChange={(e) => set("contract_value", e.target.value)} />
                )}
              </div>
            </Section>

            <Section title="Data Para Pihak" hint="Pihak Pertama (Pemberi Tugas) & Pihak Kedua (Kontraktor)" open={open.parties} onToggle={() => toggle("parties")} testid="spk-builder-sec-parties">
              <PartyCard title="PIHAK PERTAMA" subtitle="Pemberi Tugas" party={form.party1} onChange={setParty("party1")} testid="spk-builder-party1" />
              <PartyCard title="PIHAK KEDUA" subtitle="Kontraktor Pelaksana" party={form.party2} onChange={setParty("party2")} testid="spk-builder-party2" />
            </Section>

            <Section title="RAB — Rencana Anggaran Biaya" hint={`${form.rab_items.filter((r) => r.name.trim()).length} item · Grand Total ${idr(totals.grand)}`} open={open.rab} onToggle={() => toggle("rab")} testid="spk-builder-sec-rab">
              <p className="text-[11px] text-muted-foreground">Isi kolom <b>Kelompok</b> untuk membuat seksi seperti &quot;I. PEKERJAAN PERSIAPAN&quot; di PDF. Total per kelompok dihitung otomatis.</p>
              {form.rab_items.map((r, i) => (
                <div key={r.id} className="rounded-md border border-border p-2.5 space-y-2" data-testid={`spk-builder-rab-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-muted-foreground shrink-0">#{i + 1}</span>
                    <input value={r.section} onChange={(e) => updRow("rab_items", r.id, { section: e.target.value })}
                      placeholder="Kelompok (opsional), mis. Pekerjaan Persiapan" data-testid={`spk-builder-rab-section-${i}`}
                      className="flex-1 h-7 px-2 text-xs rounded-md border border-dashed border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
                    <button type="button" onClick={() => delRow("rab_items", r.id)} data-testid={`spk-builder-rab-del-${i}`}
                      className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors shrink-0">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <input value={r.name} onChange={(e) => updRow("rab_items", r.id, { name: e.target.value })}
                    placeholder="Jenis pekerjaan *" data-testid={`spk-builder-rab-name-${i}`} className={INPUT_CLS} />
                  <input value={r.spec} onChange={(e) => updRow("rab_items", r.id, { spec: e.target.value })}
                    placeholder="Spesifikasi material" data-testid={`spk-builder-rab-spec-${i}`} className={INPUT_CLS} />
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Satuan" testid={`spk-builder-rab-unit-${i}`} value={r.unit} onChange={(e) => updRow("rab_items", r.id, { unit: e.target.value })} placeholder="Ls / Kg / m3" />
                    <Field label="Volume" testid={`spk-builder-rab-volume-${i}`} type="number" min="0" step="any" value={r.volume} onChange={(e) => updRow("rab_items", r.id, { volume: e.target.value })} />
                    <Field label="Harga Satuan" testid={`spk-builder-rab-price-${i}`} type="number" min="0" value={r.unit_price} onChange={(e) => updRow("rab_items", r.id, { unit_price: e.target.value })} />
                  </div>
                  <p className="text-right text-xs text-muted-foreground" data-testid={`spk-builder-rab-total-${i}`}>
                    Total: <span className="font-semibold text-foreground">{idr(rabAmount(r))}</span>
                  </p>
                </div>
              ))}
              <button type="button" onClick={() => addRow("rab_items", newRab)} data-testid="spk-builder-rab-add"
                className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md border border-dashed border-border hover:bg-muted transition-colors w-full justify-center">
                <Plus size={13} />Tambah Item RAB
              </button>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground">Commitment Fee (Uang Muka)</label>
                  <select data-testid="spk-builder-fee-mode" value={form.commitment_fee_mode} onChange={(e) => set("commitment_fee_mode", e.target.value)} className={INPUT_CLS}>
                    <option value="none">Tidak ada</option>
                    <option value="percent">Persen dari nilai kontrak (%)</option>
                    <option value="fixed">Nominal tetap (IDR)</option>
                  </select>
                </div>
                {form.commitment_fee_mode !== "none" && (
                  <Field label={form.commitment_fee_mode === "percent" ? "Fee (%)" : "Fee (IDR)"} testid="spk-builder-fee-value" type="number" min="0" step="any" value={form.commitment_fee_value} onChange={(e) => set("commitment_fee_value", e.target.value)} />
                )}
              </div>
              <div className="rounded-md bg-muted/50 p-3 space-y-1 text-xs" data-testid="spk-builder-rab-summary">
                <p className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium" data-testid="spk-builder-subtotal">{idr(totals.subtotal)}</span></p>
                <p className="flex justify-between"><span className="text-muted-foreground">PPN {num(form.tax_percent)}% ({form.tax_included ? "termasuk" : "ditambahkan"})</span><span className="font-medium" data-testid="spk-builder-tax">{idr(totals.tax)}</span></p>
                <p className="flex justify-between text-sm border-t border-border pt-1"><span className="font-semibold">Grand Total / Nilai Kontrak</span><span className="font-bold text-[#1B2A6B] dark:text-blue-300" data-testid="spk-builder-grand-total">{idr(totals.grand)}</span></p>
                {totals.fee > 0 && (
                  <>
                    <p className="flex justify-between"><span className="text-muted-foreground">Commitment Fee{form.commitment_fee_mode === "percent" ? ` (${num(form.commitment_fee_value)}%)` : ""}</span><span className="font-medium text-[#10B981]" data-testid="spk-builder-fee-amount">{idr(totals.fee)}</span></p>
                    <p className="flex justify-between"><span className="text-muted-foreground">Sisa Pembayaran (setelah fee)</span><span className="font-medium" data-testid="spk-builder-remaining-after-fee">{idr(totals.remaining)}</span></p>
                  </>
                )}
                {payment && (
                  <>
                    <p className="flex justify-between border-t border-border pt-1"><span className="text-muted-foreground">Terbayar (Termin Proyek)</span><span className="font-medium text-[#10B981]" data-testid="spk-builder-paid">{idr(payment.paid)}</span></p>
                    <p className="flex justify-between"><span className="text-muted-foreground">Sisa Tagihan</span><span className="font-medium" data-testid="spk-builder-remaining">{idr(Math.max(0, totals.grand - payment.paid))}</span></p>
                  </>
                )}
              </div>
            </Section>

            <Section title="Termin Pembayaran (TOP)" hint={`${form.top_terms.length} termin · total ${pctSum}%`} open={open.top} onToggle={() => toggle("top")} testid="spk-builder-sec-top">
              {form.top_terms.length > 0 && Math.round(pctSum * 100) / 100 !== 100 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="spk-builder-top-warning">
                  Total persen termin saat ini {pctSum}% — idealnya 100%.
                </p>
              )}
              {totals.fee > 0 && (
                <div className="rounded-md border border-[#10B981]/40 bg-[#10B981]/5 p-2.5 flex items-center gap-2" data-testid="spk-builder-top-fee-row">
                  <span className="text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] shrink-0">Auto</span>
                  <span className="text-xs font-medium flex-1 min-w-0 truncate">Uang Muka / Commitment Fee</span>
                  <span className="text-xs font-bold text-[#10B981] shrink-0">{idr(totals.fee)}</span>
                </div>
              )}
              {form.top_terms.map((t, i) => (
                <div key={t.id} className="rounded-md border border-border p-2.5 space-y-2" data-testid={`spk-builder-top-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <select value={t.kind} onChange={(e) => updRow("top_terms", t.id, { kind: e.target.value })} data-testid={`spk-builder-top-kind-${i}`}
                      className="h-8 px-2 text-xs rounded-md border border-border bg-transparent focus-visible:outline-none">
                      <option value="dp">Uang Muka (DP)</option>
                      <option value="termin">Termin</option>
                      <option value="retention">Retensi</option>
                    </select>
                    <input value={t.label} onChange={(e) => updRow("top_terms", t.id, { label: e.target.value })}
                      placeholder="Nama tahapan *" data-testid={`spk-builder-top-label-${i}`} className={cn(INPUT_CLS, "flex-1")} />
                    <button type="button" onClick={() => delRow("top_terms", t.id)} data-testid={`spk-builder-top-del-${i}`}
                      className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors shrink-0">
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Persen (%)" testid={`spk-builder-top-percent-${i}`} type="number" min="0" max="100" step="any" value={t.percent} onChange={(e) => updRow("top_terms", t.id, { percent: e.target.value })} />
                    <Field label="Maks. hari kerja" testid={`spk-builder-top-due-${i}`} type="number" min="0" value={t.due_days} onChange={(e) => updRow("top_terms", t.id, { due_days: e.target.value })} />
                    <Field label="Syarat progress (%)" testid={`spk-builder-top-progress-${i}`} type="number" min="0" max="100" value={t.progress_req} onChange={(e) => updRow("top_terms", t.id, { progress_req: e.target.value })} />
                  </div>
                  <p className="text-right text-xs text-muted-foreground" data-testid={`spk-builder-top-amount-${i}`}>
                    Jumlah: <span className="font-semibold text-foreground">{idr(Math.round((totals.contract * num(t.percent)) / 100))}</span>
                  </p>
                </div>
              ))}
              <button type="button" onClick={() => addRow("top_terms", newTop)} data-testid="spk-builder-top-add"
                className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md border border-dashed border-border hover:bg-muted transition-colors w-full justify-center">
                <Plus size={13} />Tambah Termin
              </button>
              {form.top_terms.length > 0 && (
                <div className="rounded-md bg-muted/50 p-3 text-xs space-y-1" data-testid="spk-builder-top-summary">
                  <p className="flex justify-between"><span className="text-muted-foreground">Total alokasi termin ({pctSum}%)</span><span className="font-medium" data-testid="spk-builder-top-allocation">{idr(topAlloc)}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Grand Total / Nilai Kontrak</span><span className="font-medium">{idr(totals.contract)}</span></p>
                  <p className={cn("flex justify-between font-semibold border-t border-border pt-1", allocDiff === 0 ? "text-[#10B981]" : "text-amber-600 dark:text-amber-400")} data-testid="spk-builder-top-alloc-status">
                    <span>{allocDiff === 0 ? "Sesuai — alokasi termin sama dengan nilai kontrak" : "Selisih alokasi terhadap nilai kontrak"}</span>
                    {allocDiff !== 0 && <span>{idr(Math.abs(allocDiff))}</span>}
                  </p>
                </div>
              )}
            </Section>

            <Section title="Pasal — Klausul Perjanjian" hint={`${form.clauses.filter((c) => c.enabled).length} dari ${form.clauses.length} pasal aktif`} open={open.clauses} onToggle={() => toggle("clauses")} testid="spk-builder-sec-clauses">
              <div className="flex gap-2">
                <button type="button" onClick={() => addRow("clauses", newClause)} data-testid="spk-builder-clause-add"
                  className="h-7 px-2.5 flex items-center gap-1 text-xs font-medium rounded-md border border-dashed border-border hover:bg-muted transition-colors">
                  <Plus size={12} />Tambah Pasal
                </button>
                <button type="button" onClick={loadDefaultClauses} data-testid="spk-builder-clause-defaults"
                  className="h-7 px-2.5 text-xs rounded-md border border-border hover:bg-muted transition-colors">
                  Muat 17 Pasal Standar
                </button>
              </div>
              {clausesLoading && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="spk-builder-clauses-loading">
                  <Loader2 size={12} className="animate-spin" />Memuat 17 pasal standar...
                </p>
              )}
              {form.clauses.map((c, i) => (
                <div key={c.id} className={cn("rounded-md border p-2.5 space-y-2 transition-opacity", c.enabled ? "border-border" : "border-dashed border-border opacity-60")} data-testid={`spk-builder-clause-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={!!c.enabled} onChange={(e) => updRow("clauses", c.id, { enabled: e.target.checked })}
                      data-testid={`spk-builder-clause-enabled-${i}`} className="accent-[#1B2A6B]" />
                    <span className="text-[11px] font-semibold text-muted-foreground">Pasal {i + 1}</span>
                    <div className="ml-auto flex items-center gap-0.5">
                      <button type="button" onClick={() => moveRow("clauses", c.id, -1)} disabled={i === 0} data-testid={`spk-builder-clause-up-${i}`}
                        className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:bg-muted disabled:opacity-30 transition-colors"><ArrowUp size={12} /></button>
                      <button type="button" onClick={() => moveRow("clauses", c.id, 1)} disabled={i === form.clauses.length - 1} data-testid={`spk-builder-clause-down-${i}`}
                        className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:bg-muted disabled:opacity-30 transition-colors"><ArrowDown size={12} /></button>
                      <button type="button" onClick={() => delRow("clauses", c.id)} data-testid={`spk-builder-clause-del-${i}`}
                        className="h-6 w-6 flex items-center justify-center rounded text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors"><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <input value={c.title} onChange={(e) => updRow("clauses", c.id, { title: e.target.value })}
                    placeholder="Judul pasal, mis. LINGKUP PEKERJAAN" data-testid={`spk-builder-clause-title-${i}`} className={INPUT_CLS} />
                  <textarea rows={3} value={c.body} onChange={(e) => updRow("clauses", c.id, { body: e.target.value })}
                    placeholder="Isi pasal..." data-testid={`spk-builder-clause-body-${i}`}
                    className="w-full px-2.5 py-2 text-xs rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary resize-y" />
                </div>
              ))}
            </Section>

            <Section title="Penandatangan" hint="Semua penandatangan wajib menandatangani sebelum SPK disetujui" open={open.signers} onToggle={() => toggle("signers")} testid="spk-builder-sec-signers">
              <div className="flex flex-wrap gap-1.5" data-testid="spk-builder-signers">
                {users.map((u) => (
                  <button type="button" key={u.id} data-testid={`spk-builder-signer-${u.id}`} onClick={() => toggleSigner(u.id)}
                    className={cn("h-7 px-2.5 text-xs rounded-full border transition-colors",
                      form.required_signers.includes(u.id) ? "bg-[#1B2A6B] text-white border-[#1B2A6B]" : "border-border text-muted-foreground hover:bg-muted")}>
                    {u.name}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Kosongkan agar admin / pembuat proyek dapat menyetujui.</p>
            </Section>

            <div className="lg:hidden">
              <Section title="Preview PDF" hint={docId ? "Dokumen hasil generate" : "Simpan dulu untuk melihat preview"} open={open.preview} onToggle={() => toggle("preview")} testid="spk-builder-sec-preview">
                {previewFrame("h-[65vh]")}
              </Section>
            </div>
          </div>

          <div className="hidden lg:flex flex-col min-h-0 bg-muted/30" data-testid="spk-builder-preview-pane">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 shrink-0">
              <p className="text-xs font-semibold text-muted-foreground">Preview PDF (A4)</p>
              <span className="ml-auto text-[11px] text-muted-foreground">Klik Simpan untuk memperbarui</span>
            </div>
            <div className="flex-1 min-h-0 p-4">{previewFrame("h-full")}</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
